<template>
  <div class="dsd-root sc-page-root">
    <FiPageRail title="Roster List">
      <template #actions>
        <router-link v-if="canEditSettings" :to="{ name: 'driver_manager_dashboard', query: { settings: '1' } }" title="Driver Manager Settings" class="sc-header-gear">
          <i class="fa fa-cog"></i>
        </router-link>
      </template>
    </FiPageRail>

    <!-- date preset chips + expanding custom range; every change reloads the
         data itself (app-wide auto-refresh doctrine), so there is no manual
         refresh button. kept in sync with driver_manager_dashboard.vue's
         toolbar so the two pages read as one product surface. -->
    <div class="sc-toolbar" :class="{ 'sc-toolbar--custom': showCustomRange }">
      <div class="sc-toolbar-group dsd-date-presets" role="group" aria-label="Date range">
        <button v-for="m in quickRangeOptions" :key="'qr-' + m" type="button"
          :class="['sc-chip', { 'sc-chip--active': !customRangeOpen && activeQuickRangeMonths === m }]"
          :aria-pressed="!customRangeOpen && activeQuickRangeMonths === m ? 'true' : 'false'"
          @click="setDatePreset(m)">{{ m }}m</button>
        <button type="button" :class="['sc-chip', { 'sc-chip--active': showCustomRange }]"
          :aria-pressed="showCustomRange ? 'true' : 'false'"
          @click="setDatePreset('custom')">Custom</button>
      </div>
      <div class="dsd-date-fields">
        <date-picker class="sc-control dsd-date-field" v-model="details_fromDate" :config="optionsFrom" :placeholder="details_fromDate"/>
        <span class="dsd-date-sep">to</span>
        <date-picker class="sc-control dsd-date-field" v-model="details_toDate" :config="optionsTo" :placeholder="details_toDate"/>
      </div>
      <template v-if="!isCmTenant">
        <div class="sc-toolbar-group">
          <span class="dsd-toolbar-label">Status</span>
          <b-form-select v-model="selected_status_filter" :options="status_filter_options" class="sc-control sc-control-select"/>
        </div>
      </template>
      <b-button size="sm" variant="outline-success" v-on:click.prevent="download_all_csv()" :disabled="downloading_csv">{{ downloading_csv ? 'Preparing...' : 'Download All as CSV' }}</b-button>
    </div>

    <!-- the full-page spinner only shows before the first payload; reloads keep
         the previous render visible at reduced opacity (stale-hold) -->
    <div v-if="loading && !drivers.length && !error" style="text-align: center; margin: 50px;">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>
    <div v-else-if="error" class="text-center text-danger" style="margin: 50px;">
      <p>{{ error }}</p>
      <b-button size="sm" variant="outline-secondary" @click="load_data()">Retry</b-button>
    </div>
    <div v-else-if="!drivers.length" class="text-center text-muted" style="margin: 50px;">
      <h4>No pre-calculated data for this period.</h4>
      <p>Try a different date range.</p>
    </div>

    <div v-else :class="{ 'dsd-stale': loading }">
      <div class="dsd-panel-card">
        <div class="d-flex align-items-center flex-wrap mb-2" style="gap: 8px;">
          <div class="dsd-tier-chip-row" role="group" aria-label="Filter by tier">
            <button type="button" class="dsd-tier-chip"
              :class="{ 'dsd-tier-chip--active': !fleetTierFilter }"
              @click="fleetTierFilter = ''">All <span class="dsd-tier-chip-count">{{ totalDrivers }}</span></button>
            <button v-for="opt in tierFilterOptions.slice(1)" :key="opt.value" type="button"
              :class="['dsd-tier-chip', tierClass(opt.value, 'outline'), { 'sc-tier-outline--on': fleetTierFilter === opt.value, 'dsd-tier-chip--active': fleetTierFilter === opt.value }]"
              v-b-tooltip.hover="tierTooltip(opt.value)"
              @click="fleetTierFilter = fleetTierFilter === opt.value ? '' : opt.value">
              {{ opt.text }} <span class="dsd-tier-chip-count">{{ tierCounts[opt.value] || 0 }}</span>
            </button>
          </div>
          <span v-if="user.role == 'admin' || user.role == 'customer-admin' || user.role == 'tbn-admin'" class="text-muted" style="font-size: 0.75rem;">
            Configurable in <router-link v-if="canEditSettings" :to="{ name: 'driver_manager_dashboard', query: { settings: '1', settings_tab: 'coloring' } }">Driver Manager Settings</router-link><template v-else>Driver Manager Settings</template> under Conditional Coloring.
          </span>
        </div>
        <div class="d-flex align-items-center flex-wrap mb-2" style="gap: 10px;">
          <b-form-input v-model="fleetSearch" placeholder="Search driver..." class="dsd-filter-control" style="max-width: 220px;" size="sm"/>
          <b-form-select v-if="mobileView" v-model="mobileSortKey" :options="mobileSortOptions" size="sm" class="dsd-filter-control" style="max-width: 220px;"/>
          <span class="text-muted ml-auto" style="font-size: 0.8rem;">
            {{ filteredFleetTable.length }} of {{ drivers.length }} drivers
          </span>
        </div>

        <!-- the table expands with the page (no inner scroll region); the
             responsive wrapper below keeps narrow viewports x-scrolling the
             table instead of the page -->
        <div v-if="!mobileView" class="dsd-fleet-table-wrap">
        <b-table
          :items="filteredFleetTable"
          :fields="fleetTableFields"
          small hover responsive
          sort-by="compiled_score"
          :sort-desc="true"
          :sort-compare="tableSortCompare"
          @row-clicked="row => openDriver(row.driver_id)"
          class="dsd-fleet-table"
        >
          <template slot="driver_name" slot-scope="data">
            <span>{{ data.value }}</span>
            <span v-if="isActive(data.item) && criticalFlagCount(data.item) > 0" class="text-danger" title="Critical flags">&nbsp;⚠</span>
          </template>
          <template slot="compiled_score" slot-scope="data">
            <div class="dsd-sbar">
          <span class="dsd-sbar-val">{{ formatNumber(data.value) }}</span>
            <div class="dsd-sbar-bg"><div class="dsd-sbar-fill" :class="barClass(data.item.compiled_score_tier)" :style="{ width: Math.min(data.value || 0, 100) + '%' }"></div></div>
          </div>
          </template>
          <template slot="compiled_score_tier" slot-scope="data">
            <span :class="getCompiledTierBadgeClass(data.value)">{{ data.value }}</span>
          </template>
          <template slot="risk_score" slot-scope="data"><span :class="bandCellClass('risk_score', data.item)">{{ formatNumber(data.value) }}</span></template>
          <template slot="speeding_percentage" slot-scope="data"><span :class="bandCellClass('speeding_percentage', data.item)">{{ formatNumber(data.value) }}%</span></template>
          <template slot="distracted_driving_per_10k_miles" slot-scope="data"><span :class="bandCellClass('distracted_driving_per_10k_miles', data.item)">{{ formatNumber(data.value) }}</span></template>
          <template slot="following_distance_per_10k_miles" slot-scope="data"><span :class="bandCellClass('following_distance_per_10k_miles', data.item)">{{ formatNumber(data.value) }}</span></template>
          <template slot="harsh_braking_per_10k_miles" slot-scope="data"><span :class="bandCellClass('harsh_braking_per_10k_miles', data.item)">{{ formatNumber(data.value) }}</span></template>
          <template slot="idling_percentage" slot-scope="data"><span :class="bandCellClass('idling_percentage', data.item)">{{ formatNumber(data.value) }}%</span></template>
          <template slot="miles_per_gallon" slot-scope="data"><span :class="bandCellClass('miles_per_gallon', data.item)">{{ formatNumber(data.value) }}</span></template>
          <template slot="dvir_completion_rate" slot-scope="data"><span :class="bandCellClass('dvir_completion_rate', data.item)">{{ data.value == null ? '--' : formatNumber(data.value) + '%' }}</span></template>
          <template slot="total_miles" slot-scope="data">{{ formatNumber(data.value) }}</template>
          <template slot="bus_days" slot-scope="data"><span :class="bandCellClass('bus_days', data.item)">{{ formatNumber(data.value) }}</span></template>
          <template slot="customer_requests_per_10k_miles" slot-scope="data"><span :class="bandCellClass('customer_requests_per_10k_miles', data.item)">{{ formatNumber(data.value) }}</span></template>
          <template slot="weekend_availability_percentage" slot-scope="data"><span :class="bandCellClass('weekend_availability_percentage', data.item)">{{ formatNumber(data.value) }}%</span></template>
        </b-table>
        </div>

        <!-- Mobile card list: same data as the table above, tappable to open the driver profile -->
        <div v-else class="dsd-fleet-cards">
          <div
            v-for="row in sortedMobileFleetTable"
            :key="row.driver_id || row.driver_name"
            class="dsd-fleet-card"
            @click="openDriver(row.driver_id)"
          >
            <div class="dsd-fleet-card-top">
              <div class="dsd-fleet-card-name">
                {{ row.driver_name }}
                <span v-if="isActive(row) && criticalFlagCount(row) > 0" class="text-danger" title="Critical flags">&nbsp;⚠</span>
              </div>
              <span :class="getCompiledTierBadgeClass(row.compiled_score_tier)">{{ row.compiled_score_tier }}</span>
            </div>
            <div class="dsd-sbar mt-1">
              <span class="dsd-sbar-val">{{ formatNumber(row.compiled_score) }}</span>
              <div class="dsd-sbar-bg"><div class="dsd-sbar-fill" :class="barClass(row.compiled_score_tier)" :style="{ width: Math.min(row.compiled_score || 0, 100) + '%' }"></div></div>
            </div>
            <div class="dsd-fleet-card-kpis">
              <div><span class="dsd-fleet-card-kpi-label">Safety</span> {{ formatNumber(row.risk_score) }}</div>
              <div><span class="dsd-fleet-card-kpi-label">Idle%</span> {{ formatNumber(row.idling_percentage) }}%</div>
              <div><span class="dsd-fleet-card-kpi-label">MPG</span> {{ formatNumber(row.miles_per_gallon) }}</div>
              <div><span class="dsd-fleet-card-kpi-label">DVIR%</span> {{ row.dvir_completion_rate == null ? '—' : formatNumber(row.dvir_completion_rate) + '%' }}</div>
            </div>
          </div>
          <div v-if="!filteredFleetTable.length" class="text-center text-muted" style="padding: 30px 0;">No drivers match the current filters.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import axios from 'axios'
import { justDate } from '../helpers/dates'
import { tbn_api_url, get_all_driver_scorecards } from '../networking/tbn_apis'
import { api_url as cm_api_url, cm_get_all_driver_scorecards } from '../networking/apis'
import { mergeThresholds, getFieldBand, tierClass } from '../utils/driver_manager_thresholds'
import { buildFirstOfMonthDates, buildLastOfMonthOrTodayDates, defaultScorecardRange } from '../utils/driver_manager_dates'
import { buildBadnessPools, getDriverFlags, isActiveDriver } from '../utils/scorecard_flags'

// Moved out of driver_manager_dashboard.vue's Roster Overview tab into its own
// top-level page (same split FI-2064 gave Individual Driver and Compare) — the
// dashboard keeps the fleet-wide charts/tiles, this page owns the sortable
// per-driver leaderboard table. The dsd-* class names are retained so the
// styling stays byte-identical with what the dashboard used to render.

const TIER_ORDER = ['Elite', 'Solid', 'Fair', 'High Risk']
const INSUFFICIENT_DATA_TIER = 'Insufficient Data'
// Shown in the tier filter chip tooltips — the "what does each tier mean"
// answer for auditors. Kept in sync with driver_manager_dashboard.vue's copy.
const TIER_DEFINITIONS = [
  { tier: 'Elite', desc: 'Combined score at or above the Elite threshold (default 80/100). Top-performing drivers across weighted KPIs.' },
  { tier: 'Solid', desc: 'Combined score at or above the Solid threshold (default 60/100). Meeting expectations across weighted KPIs.' },
  { tier: 'Fair', desc: 'Combined score at or above the Fair threshold (default 35/100). Below target on one or more weighted KPIs; a coaching candidate.' },
  { tier: 'High Risk', desc: 'Combined score below the Fair threshold. Meaningfully underperforming on weighted KPIs and should be prioritized for coaching.' },
  { tier: INSUFFICIENT_DATA_TIER, desc: '500 miles or fewer AND 5 bus days or fewer in the selected period. Not enough activity to fairly score or rank this driver.' },
]
const ALL_TIERS = [...TIER_ORDER, INSUFFICIENT_DATA_TIER]

// quick-select date presets in months (scorecards are pre-calculated monthly
// rather than daily); 12 is the server-side range cap. the toolbar renders
// these as chips plus a Custom option that reveals month-snapped pickers.
const QUICK_RANGE_MONTHS = [1, 3, 6, 12]

export default {
  name: 'driver_manager_roster_list',

  data() {
    return {
      loading: false,
      downloading_csv: false,
      error: null,
      drivers: [],
      scorecard_thresholds: null,
      customRangeOpen: false,
      mobileView: false,
      mobileSortKey: 'compiled_score_desc',
      fleetSearch: '',
      fleetTierFilter: '',
      selected_status_filter: null,
      // default window shared with the dashboard and the Coaching Portal's
      // flagged panel via defaultScorecardRange
      details_fromDate: justDate(defaultScorecardRange().from),
      details_toDate: justDate(defaultScorecardRange().to),
    }
  },

  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    mergedThresholds() {
      return mergeThresholds(this.scorecard_thresholds)
    },
    canEditSettings() {
      return ['admin', 'customer-admin', 'tbn-admin'].includes(this.user.role)
    },
    isCmTenant() {
      return (this.user && this.user.types && this.user.types[this.user.tenant]) === 'CM'
    },
    totalDrivers() {
      return this.drivers.length
    },
    tierCounts() {
      const counts = { Elite: 0, Solid: 0, 'Fair': 0, 'High Risk': 0, 'Insufficient Data': 0 }
      this.drivers.forEach(d => {
        if (counts[d.compiled_score_tier] !== undefined) counts[d.compiled_score_tier]++
      })
      return counts
    },
    tierDefinitions() {
      return TIER_DEFINITIONS
    },
    tierFilterOptions() {
      return [
        { value: '', text: 'All Tiers' },
        { value: 'Elite', text: 'Elite' },
        { value: 'Solid', text: 'Solid' },
        { value: 'Fair', text: 'Fair' },
        { value: 'High Risk', text: 'High Risk' },
        { value: 'Insufficient Data', text: 'Insufficient Data' },
      ]
    },
    status_filter_options() {
      const statuses = [...new Set(this.drivers.map(d => d.employment_status).filter(s => s && s.trim()))].sort()
      return [
        { value: null, text: 'All Statuses' },
        ...statuses.map(s => ({ value: s, text: s })),
      ]
    },
    activeDrivers() {
      // Same activity gate used for flags/percentiles: excludes drivers with negligible data in the period.
      return this.drivers.filter(d => this.isActive(d))
    },
    // Pre-sorted ascending value pools per flagged metric, memoized off
    // activeDrivers — see driver_manager_dashboard.vue for why this is a
    // computed rather than sorted inside each getFlags() call.
    badnessPools() {
      return buildBadnessPools(this.activeDrivers)
    },
    statusFilteredDrivers() {
      if (!this.selected_status_filter) return this.drivers
      return this.drivers.filter(d => d.employment_status === this.selected_status_filter)
    },
    fleetTableFields() {
      const fields = [
        { key: 'driver_name', label: 'Driver', sortable: true },
        { key: 'compiled_score', label: 'Combined', sortable: true },
        { key: 'compiled_score_tier', label: 'Tier', sortable: true },
        { key: 'risk_score', label: 'Safety', sortable: true },
        { key: 'speeding_percentage', label: 'Speed%', sortable: true },
        { key: 'distracted_driving_per_10k_miles', label: 'Distracted/10k', sortable: true },
        { key: 'following_distance_per_10k_miles', label: 'Following/10k', sortable: true },
        { key: 'harsh_braking_per_10k_miles', label: 'Harsh Brake/10k', sortable: true },
        { key: 'idling_percentage', label: 'Idle%', sortable: true },
        { key: 'miles_per_gallon', label: 'MPG', sortable: true },
        { key: 'dvir_completion_rate', label: 'DVIR%', sortable: true },
        { key: 'total_miles', label: 'Miles', sortable: true },
        { key: 'bus_days', label: 'Days', sortable: true },
      ]
      if (!this.isCmTenant) {
        fields.push({ key: 'customer_requests_per_10k_miles', label: 'Cust Req/10k', sortable: true })
        fields.push({ key: 'weekend_availability_percentage', label: 'Wknd%', sortable: true })
      }
      return fields
    },
    filteredFleetTable() {
      let data = [...this.statusFilteredDrivers]
      if (this.fleetTierFilter) {
        data = data.filter(d => d.compiled_score_tier === this.fleetTierFilter)
      }
      if (this.fleetSearch) {
        const q = this.fleetSearch.toLowerCase()
        data = data.filter(d => (d.driver_name || '').toLowerCase().includes(q))
      }
      return data
    },
    mobileSortOptions() {
      return [
        { value: 'compiled_score_desc', text: 'Sort: Combined Score (High to Low)' },
        { value: 'compiled_score_asc', text: 'Sort: Combined Score (Low to High)' },
        { value: 'driver_name_asc', text: 'Sort: Name (A-Z)' },
        { value: 'risk_score_desc', text: 'Sort: Safety (High to Low)' },
        { value: 'risk_score_asc', text: 'Sort: Safety (Low to High)' },
      ]
    },
    // b-table's column sort disappears in mobile card mode, so this reproduces the
    // same ordering via a plain select — kept separate from tableSortCompare since
    // there are no column headers to click here.
    sortedMobileFleetTable() {
      const [field, dir] = this.mobileSortKey.split(/_(asc|desc)$/).filter(Boolean)
      const desc = dir === 'desc'
      const data = [...this.filteredFleetTable]
      data.sort((a, b) => {
        if (field === 'driver_name') {
          const av = (a.driver_name || '').toLowerCase()
          const bv = (b.driver_name || '').toLowerCase()
          return desc ? bv.localeCompare(av) : av.localeCompare(bv)
        }
        const av = a[field] == null ? -Infinity : Number(a[field])
        const bv = b[field] == null ? -Infinity : Number(b[field])
        return desc ? bv - av : av - bv
      })
      return data
    },
    quickRangeOptions() {
      return QUICK_RANGE_MONTHS
    },
    // Highlights whichever quick-range button (if any) matches the current from/to
    // dates exactly, so manually-picked dates that happen to land on a 1m/3m/6m
    // boundary still light up, and custom ranges show no button as active.
    activeQuickRangeMonths() {
      for (const months of QUICK_RANGE_MONTHS) {
        const { from, to } = this.quickRangeDates(months)
        if (from === this.details_fromDate && to === this.details_toDate) return months
      }
      return null
    },
    // the custom pickers stay revealed while explicitly opened, and reveal
    // themselves when the current range matches no preset (deep link, clamped
    // range) so the active dates are never invisible
    showCustomRange() {
      return this.customRangeOpen || this.activeQuickRangeMonths === null
    },
    // month-granular data: the pickers only offer first-of-month "from" and
    // last-of-month (or today) "to" dates, same as the dashboard's pickers
    optionsFrom() {
      const minD = moment().subtract(3, 'years').startOf('month')
      const maxD = moment()
      return {
        format: 'MM-DD-YYYY', useCurrent: false, widgetPositioning: { horizontal: 'left', vertical: 'bottom' },
        minDate: minD, maxDate: maxD, enabledDates: buildFirstOfMonthDates(minD, maxD),
        keepOpen: false, showClear: false, showClose: true,
      }
    },
    optionsTo() {
      const minD = moment().subtract(3, 'years').startOf('month')
      const maxD = moment()
      return {
        format: 'MM-DD-YYYY', useCurrent: false, widgetPositioning: { horizontal: 'left', vertical: 'bottom' },
        minDate: minD, maxDate: maxD, enabledDates: buildLastOfMonthOrTodayDates(minD, maxD),
        keepOpen: false, showClear: false, showClose: true,
      }
    },
  },

  watch: {
    // the active tier chip round-trips through ?tier= so the dashboard's Tier
    // Breakdown chart can deep-link a pre-filtered roster, and a filtered view
    // survives reload; no chip keeps a clean url
    fleetTierFilter(tier) {
      const current = this.$route.query.tier || ''
      if (current === tier) return
      const query = { ...this.$route.query }
      if (tier) query.tier = tier
      else delete query.tier
      this.$router.replace({ query }).catch(() => {})
    },
    '$route.query.tier'() {
      this.hydrateTierFromRoute()
    },
    // both date watchers end by scheduling a reload; corrective re-assignments
    // retrigger the watcher, and scheduleRangeLoad coalesces the cascade into
    // one load_data per user action. an unparseable value (cleared/typed-out
    // field) bails outright so "Invalid date" can never snap-loop or reach the
    // api as a month string
    details_toDate() {
      const today = moment()
      const to = moment(this.details_toDate, 'MM-DD-YYYY')
      if (!to.isValid()) return
      if (to.isAfter(today)) { this.details_toDate = justDate(today); return }
      // month-granular data: snap a mid-month pick to the month end (today is
      // allowed within the current month, matching the picker's enabledDates)
      if (!to.isSame(moment(to).endOf('month'), 'day') && !to.isSame(today, 'day')) {
        this.details_toDate = justDate(moment.min(moment(to).endOf('month'), today))
        return
      }
      if (moment(this.details_fromDate, 'MM-DD-YYYY') > to) {
        this.details_fromDate = justDate(moment(to).startOf('month'))
      }
      const months = to.diff(moment(this.details_fromDate, 'MM-DD-YYYY'), 'months', true)
      if (months > 12) this.details_fromDate = moment(to).subtract(12, 'months').startOf('month').format('MM-DD-YYYY')
      this.scheduleRangeLoad()
    },
    details_fromDate() {
      const from = moment(this.details_fromDate, 'MM-DD-YYYY')
      if (!from.isValid()) return
      if (!from.isSame(moment(from).startOf('month'), 'day')) {
        this.details_fromDate = justDate(moment(from).startOf('month'))
        return
      }
      if (from > moment(this.details_toDate, 'MM-DD-YYYY')) {
        this.details_toDate = justDate(moment.min(moment(from).endOf('month'), moment()))
      }
      const months = moment(this.details_toDate, 'MM-DD-YYYY').diff(from, 'months', true)
      if (months > 12) this.details_toDate = moment(from).add(12, 'months').endOf('month').format('MM-DD-YYYY')
      this.scheduleRangeLoad()
    },
  },

  created() {
    this.hydrateTierFromRoute()
    this.setupMobileQuery()
    this.load_data()
  },

  beforeDestroy() {
    this.teardownMobileQuery()
  },

  methods: {
    hydrateTierFromRoute() {
      const tier = this.$route.query.tier || ''
      if (tier !== this.fleetTierFilter && (tier === '' || ALL_TIERS.includes(tier))) {
        this.fleetTierFilter = tier
      }
    },

    // "What does this tier mean" — surfaced as a hover tooltip directly on each
    // filter chip.
    tierTooltip(tier) {
      const def = this.tierDefinitions.find(d => d.tier === tier)
      return def ? def.desc : ''
    },
    // reactive to resize/rotation (unlike a one-time user-agent sniff), mirroring
    // the pattern used by vsm_board.vue's mobile card-list breakpoint
    setupMobileQuery() {
      if (typeof window === 'undefined' || !window.matchMedia) return
      this._mql = window.matchMedia('(max-width: 768px)')
      this.mobileView = this._mql.matches
      this._mqlHandler = e => { this.mobileView = e.matches }
      if (this._mql.addEventListener) this._mql.addEventListener('change', this._mqlHandler)
      else this._mql.addListener(this._mqlHandler)
    },
    teardownMobileQuery() {
      if (!this._mql) return
      if (this._mql.removeEventListener) this._mql.removeEventListener('change', this._mqlHandler)
      else this._mql.removeListener(this._mqlHandler)
    },

    async load_data() {
      // sequence guard: with auto-refresh a slow response can arrive after a
      // newer request; only the latest request may write state
      const seq = (this._loadSeq = (this._loadSeq || 0) + 1)
      this.loading = true
      this.error = null
      try {
        const start_month = moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM')
        const end_month = moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM')
        const response = this.isCmTenant
          ? await cm_get_all_driver_scorecards(this.user.tenant, start_month, end_month)
          : await get_all_driver_scorecards(this.user.tenant, start_month, end_month)
        if (seq !== this._loadSeq) return
        const responseData = (response && response.value && response.value.data) || (response && response.data)
        if (responseData && responseData.error) {
          this.error = responseData.error
          this.drivers = []
        } else if (responseData) {
          this.drivers = (responseData.drivers || []).map(d => this.normalizeDriverRow(d))
          this.scorecard_thresholds = responseData.scorecard_thresholds !== undefined ? responseData.scorecard_thresholds : null
        } else {
          this.error = 'Unexpected response format from server'
        }
      } catch (e) {
        if (seq !== this._loadSeq) return
        const serverMsg = (e.response && e.response.data && e.response.data.error)
          || (e.response && e.response.data && e.response.data.data && e.response.data.data.error)
        this.error = serverMsg || e.message || 'Error loading fleet driver data'
      } finally {
        if (seq === this._loadSeq) this.loading = false
      }
    },

    // "Last full month" mirrors the same >=7th-of-month cutover the default range
    // uses: early in a month, the just-finished month's pre-calc may not have landed
    // yet, so roll back one extra month rather than land on an empty period.
    _lastFullMonthEnd() {
      return moment().date() >= 7 ? moment().subtract(1, 'month') : moment().subtract(2, 'months')
    },
    quickRangeDates(months) {
      const to = this._lastFullMonthEnd().endOf('month')
      const from = to.clone().subtract(months - 1, 'months').startOf('month')
      return { from: justDate(from), to: justDate(to) }
    },
    setDatePreset(preset) {
      if (preset === 'custom') {
        this.customRangeOpen = true
        return
      }
      this.customRangeOpen = false
      const { from, to } = this.quickRangeDates(preset)
      // assigning triggers the date watchers, which schedule the reload;
      // unchanged dates fire nothing, so re-clicking the active chip is free
      this.details_fromDate = from
      this.details_toDate = to
    },
    // coalesces the watcher cascade (snap, cross-field clamp) into a single
    // reload per user action
    scheduleRangeLoad() {
      if (this._rangeLoadScheduled) return
      this._rangeLoadScheduled = true
      this.$nextTick(() => {
        this._rangeLoadScheduled = false
        this.load_data()
      })
    },

    async download_all_csv() {
      const start_month = moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM')
      const end_month = moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM')
      this.downloading_csv = true
      try {
        const statusParam = (!this.isCmTenant && this.selected_status_filter) ? `&status_filter=${encodeURIComponent(this.selected_status_filter)}` : ''
        const csvUrl = this.isCmTenant
          ? `${cm_api_url}bookings/driver-scorecard/all/csv?tenant=${this.user.tenant}&start_month=${start_month}&end_month=${end_month}`
          : `${tbn_api_url}tbn/driver-scorecard/all/csv?tenant=${this.user.tenant}&start_month=${start_month}&end_month=${end_month}${statusParam}`
        const response = await axios.get(
          csvUrl,
          { headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` }, responseType: 'blob' }
        )
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        const prefix = this.isCmTenant ? 'cm-driver-scorecard' : 'driver-scorecard'
        link.setAttribute('download', `${prefix}-${start_month}-to-${end_month}.csv`)
        document.body.appendChild(link)
        link.click()
        link.remove()
      } catch (e) {
        this.$bvToast.toast('Could not download driver data.', { variant: 'danger' })
      } finally {
        this.downloading_csv = false
      }
    },

    // deep-links to the Individual Driver page with the driver preselected
    openDriver(driverId) {
      this.$router.push({ name: 'driver_manager_individual', query: { driver_id: driverId } })
    },

    isActive(d) {
      return isActiveDriver(d)
    },

    // CM has no dispatch-route-mileage field like TBN's total_miles — it only has
    // Samsara telematics odometer mileage (miles_driven). Map that onto total_miles
    // so every other total_miles reference in this component works unmodified for
    // both tenant types.
    normalizeDriverRow(row) {
      if (!row) return row
      if (this.isCmTenant && row.total_miles == null && row.miles_driven != null) {
        return { ...row, total_miles: row.miles_driven }
      }
      return row
    },

    // b-table sorts strings alphabetically by default, which scrambles tier order
    // (e.g. "Elite" < "High Risk" < "Fair" < "Solid"). Rank tiers worst
    // to best so ascending sort reads worst→best and descending reads best→worst;
    // returning null for every other column falls through to b-table's default compare.
    tableSortCompare(aRow, bRow, key) {
      if (key === 'compiled_score_tier') {
        const rank = { 'Insufficient Data': -1, 'High Risk': 0, 'Fair': 1, Solid: 2, Elite: 3 }
        const aRank = rank.hasOwnProperty(aRow.compiled_score_tier) ? rank[aRow.compiled_score_tier] : -1
        const bRank = rank.hasOwnProperty(bRow.compiled_score_tier) ? rank[bRow.compiled_score_tier] : -1
        return aRank - bRank
      }
      return null
    },

    barClass(tier) {
      return tierClass(tier, 'fill')
    },

    getCompiledTierBadgeClass(tier) {
      return tierClass(tier, 'chip')
    },

    // shared tier class helper exposed to the template (filter chips)
    tierClass,

    // conditional-coloring band dot for a leaderboard metric cell. fields
    // whose table key differs from their threshold key map here (distracted
    // driving is stored per 10k miles but configured as
    // distracted_driving_percentage, same split as FLAG_METRICS). low-activity
    // drivers stay dotless -- their metrics are renormalized artifacts, the
    // same reason Insufficient Data scores stay uncolored.
    bandCellClass(fieldKey, item) {
      if (item.compiled_score_tier === INSUFFICIENT_DATA_TIER) return ''
      const thresholdKey = fieldKey === 'distracted_driving_per_10k_miles' ? 'distracted_driving_percentage' : fieldKey
      const band = getFieldBand(thresholdKey, item[fieldKey], this.mergedThresholds)
      return band ? `sc-band sc-band--${band.key}` : ''
    },

    getFlags(d) {
      return getDriverFlags(d, {
        pools: this.badnessPools,
        activeDrivers: this.activeDrivers,
        thresholds: this.mergedThresholds,
      })
    },

    criticalFlagCount(d) {
      return this.getFlags(d).filter(f => f.severity === 'critical').length
    },

    formatNumber(value, decimals = 2) {
      if (value === null || value === undefined || isNaN(Number(value))) return '—'
      const fixed = Number(value).toFixed(decimals)
      // A value that rounds to a whole number ("85.00") shows as "85" -- the
      // trailing zeros are just noise. A genuinely fractional value ("85.32")
      // keeps its decimals.
      const allZeroDecimals = decimals > 0 && fixed.endsWith('.' + '0'.repeat(decimals))
      return allZeroDecimals ? fixed.slice(0, -(decimals + 1)) : fixed
    },
  },
}
</script>

<style scoped>
.dsd-tier-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.dsd-tier-chip {
  background: var(--fi-surface, #fff);
  border: 1.5px solid var(--fi-line, #ccc);
  color: var(--fi-text-muted, #666);
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
}
.dsd-tier-chip:hover {
  filter: brightness(0.95);
}
.dsd-tier-chip--active {
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.08) inset;
}
.dsd-tier-chip-count {
  font-weight: 400;
  opacity: 0.85;
}
/* date preset chips (od-chip recipe, token-native so midnight needs no
   overrides) + the custom-range reveal driven by .sc-toolbar--custom --
   kept in sync with the identical block in driver_manager_dashboard.vue
   since scoped styles don't share across components */
.dsd-date-presets {
  gap: 6px;
}
.sc-chip {
  background: var(--fi-surface, #f8fafc);
  border: 1px solid var(--fi-line, #d6dde6);
  border-radius: var(--fi-radius-pill, 999px);
  padding: 4px 12px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--fi-text-muted, #4b5563);
  cursor: pointer;
  transition: background .12s, border-color .12s, color .12s;
}
.sc-chip:hover {
  background: var(--fi-surface-sunken, #eef4fb);
}
.sc-chip--active {
  background: var(--fi-ops-accent, #3a6e99);
  border-color: var(--fi-ops-accent, #3a6e99);
  color: #fff;
}
.dsd-date-fields {
  display: none;
}
.sc-toolbar--custom .dsd-date-fields {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.dsd-date-field {
  width: 122px;
  font-size: 0.9rem;
}
/* ios safari zooms the page on focusing any input under 16px */
@media (max-width: 768px) {
  .dsd-date-field {
    font-size: 16px;
  }
}
.dsd-date-sep,
.dsd-toolbar-label {
  color: var(--fi-text-muted, #6c757d);
  font-size: 0.85rem;
}
/* reloads keep the previous render visible at reduced opacity instead of
   flashing back to a spinner (stale-hold, ops_desk_analytics precedent) */
.dsd-stale {
  opacity: 0.55;
  transition: opacity 0.15s;
}

.dsd-panel-card {
  background: var(--fi-surface, #fff);
  border: 1px solid var(--fi-line, #dee2e6);
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 25px;
}

.dsd-fleet-table {
  cursor: pointer;
}
/* the rest of the fleet-table styling (dense cells, page-sticky thead, sort
   arrows) lives in the unscoped style block at the end of this file -- those
   rules target nodes bootstrap-vue renders internally, which scoped
   [data-v] selectors can't reach */

.dsd-sbar {
  display: flex;
  align-items: center;
  gap: 7px;
}
.dsd-sbar-val {
  min-width: 34px;
  display: inline-block;
  font-weight: 700;
  flex-shrink: 0;
  text-align: right;
}
.dsd-sbar-bg {
  width: 70px;
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  flex-shrink: 1;
  min-width: 30px;
}
.dsd-sbar-fill {
  height: 6px;
  border-radius: 3px;
}

.dsd-fleet-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.dsd-fleet-card {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 10px 12px;
  background: #fff;
  cursor: pointer;
}
.dsd-fleet-card:active {
  background: #f4f6f8;
}
.dsd-fleet-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-weight: bold;
}
.dsd-fleet-card-name {
  font-size: 1rem;
}
.dsd-fleet-card-kpis {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 4px 10px;
  margin-top: 8px;
  font-size: 0.85rem;
}
.dsd-fleet-card-kpi-label {
  color: #888;
  font-size: 0.75rem;
  margin-right: 4px;
}

@media (max-width: 768px) {
  .dsd-filter-control {
    width: 100% !important;
    max-width: 100% !important;
  }

  /* mobile card list: score value was a fixed 32px box that didn't grow to fit
     its own text (e.g. "87.5"), so it overflowed and sat on top of the bar
     next to it. */
  .dsd-sbar {
    gap: 6px;
  }
  .dsd-sbar-val {
    font-size: 0.85rem;
  }
  .dsd-sbar-bg {
    max-width: 60px;
  }
}
</style>

<style>
/* ---- fleet leaderboard table (support_ticket_list dense-table pattern) ----
   unscoped: the .table/.b-table/thead nodes are rendered inside bootstrap-vue,
   so scoped selectors can't reach them. the !important cell padding must
   out-rank the global `.table td { padding: 0.1rem !important }` leak from
   older unscoped component stylesheets. */
.dsd-fleet-table-wrap {
  margin: 4px 0 20px;
}
.dsd-fleet-table-wrap .table.b-table {
  font-size: 13px;
  border-collapse: collapse;
  margin-bottom: 0;
}
.dsd-fleet-table-wrap .table.b-table th,
.dsd-fleet-table-wrap .table.b-table td {
  border-left: none !important;
  border-right: none !important;
  border-top: none !important;
  border-bottom: 1px solid var(--fi-line, #e0e0e0);
  padding: 3px 8px !important;
  vertical-align: middle !important;
  text-align: left !important;
}
.dsd-fleet-table-wrap .table.b-table th:first-child,
.dsd-fleet-table-wrap .table.b-table td:first-child {
  padding-left: 12px !important;
}
.dsd-fleet-table-wrap .table.b-table th:last-child,
.dsd-fleet-table-wrap .table.b-table td:last-child {
  padding-right: 12px !important;
}
.dsd-fleet-table-wrap .table.b-table thead th {
  background: var(--fi-surface-sunken, #f8f9fa);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: var(--fi-text-muted, #555);
  position: sticky;
  top: 0;
  z-index: 2;
  /* box-shadow, not border-bottom: border-bottom disappears on sticky th
     under border-collapse: collapse, and ::after is taken by the sort arrows */
  box-shadow: inset 0 -2px 0 0 var(--fi-line-strong, #ccc);
}
.dsd-fleet-table-wrap .table.b-table tbody tr:hover td {
  background-color: var(--fi-brand-tint, #f0f5ff);
}

/* sort arrows: replace bootstrap-vue's floated background-image icons with
   inline glyphs that are always visible on sortable columns */
.dsd-fleet-table-wrap th[aria-sort] {
  background-image: none !important;
  padding-right: 0.75rem !important;
  white-space: nowrap;
}
.dsd-fleet-table-wrap th[aria-sort]::before {
  display: none !important;
}
.dsd-fleet-table-wrap th[aria-sort]::after {
  display: inline-block !important;
  margin-left: 4px;
  font-size: 10px;
  color: #999;
  vertical-align: middle;
  float: none !important;
  width: auto !important;
  height: auto !important;
  opacity: 1 !important;
}
.dsd-fleet-table-wrap th[aria-sort="none"]::after { content: "\2195" !important; }
.dsd-fleet-table-wrap th[aria-sort="ascending"]::after { content: "\25B2" !important; color: var(--fi-rail-accent, steelblue); }
.dsd-fleet-table-wrap th[aria-sort="descending"]::after { content: "\25BC" !important; color: var(--fi-rail-accent, steelblue); }

/* bootstrap-vue's responsive wrapper is a scroll container, which kills
   position:sticky against the page. wide desktops fit every column, so clip
   there -- overflow-x: clip does not create a scroll container, letting the
   thead genuinely stick to the viewport; narrower viewports keep the x-scroll
   wrapper (where the sticky header is decorative, support_ticket_list parity) */
@media (min-width: 1200px) {
  .dsd-fleet-table-wrap .table-responsive {
    overflow-x: clip;
  }
}

/* tier filter chips, outlined variant (tierClass(tier, 'outline')): the
   .dsd-root prefix lifts these above the scoped .dsd-tier-chip base, which
   sets token border/ink for the neutral "All" chip and would otherwise
   out-rank a bare single-class rule via its scoped attribute selector */
.dsd-root .dsd-tier-chip.sc-tier-outline--elite        { border-color: #28a745; color: #1e7e34; }
.dsd-root .dsd-tier-chip.sc-tier-outline--solid        { border-color: #007bff; color: #0062cc; }
.dsd-root .dsd-tier-chip.sc-tier-outline--coaching     { border-color: #d39e00; color: #a07800; }
.dsd-root .dsd-tier-chip.sc-tier-outline--highrisk     { border-color: #dc3545; color: #bd2130; }
.dsd-root .dsd-tier-chip.sc-tier-outline--insufficient { border-color: #adb5bd; color: #6c757d; }
.dsd-root .sc-tier-outline--on.sc-tier-outline--elite        { background: #28a745; border-color: #28a745; color: #fff; }
.dsd-root .sc-tier-outline--on.sc-tier-outline--solid        { background: #007bff; border-color: #007bff; color: #fff; }
.dsd-root .sc-tier-outline--on.sc-tier-outline--coaching     { background: #ffc107; border-color: #ffc107; color: #212529; }
.dsd-root .sc-tier-outline--on.sc-tier-outline--highrisk     { background: #dc3545; border-color: #dc3545; color: #fff; }
.dsd-root .sc-tier-outline--on.sc-tier-outline--insufficient { background: #adb5bd; border-color: #adb5bd; color: #fff; }
</style>
